import { TareasPendientes } from '../components/TareasPendientes';
import { Link, useNavigate } from 'react-router-dom';
import { CabeceraPagina } from '../components/Armazon';
import { Boton, Cargando, Pildora, Tarjeta, Vacio } from '../components/ui';
import { api } from '../lib/api';
import { ESTADO_ETIQUETA, fecha, franja } from '../lib/formato';
import { useDatos, useSesion } from '../lib/sesion';
import type { Estado, EventoAgenda, SolicitudResumen } from '../lib/tipos';

/** Qué estados reclaman acción de cada rol: es lo que se pone primero. */
const BANDEJA: Record<string, { estados: Estado[]; titulo: string; vacio: string }> = {
  SALESPERSON: {
    estados: ['DRAFT'],
    titulo: 'Borradores sin enviar',
    vacio: 'No tiene borradores pendientes.',
  },
  SALES_MANAGER: {
    estados: ['SENT', 'RECEIVED'],
    titulo: 'Solicitudes esperando su decisión',
    vacio: 'No hay solicitudes pendientes de valorar.',
  },
  ADMIN: {
    estados: ['SENT', 'RECEIVED'],
    titulo: 'Solicitudes en curso',
    vacio: 'No hay solicitudes en curso.',
  },
  COOK: {
    estados: ['AUTHORIZED'],
    titulo: 'Visitas pendientes de plan de cocina',
    vacio: 'No hay planes de cocina pendientes.',
  },
};

export function Panel() {
  const { usuario } = useSesion();
  const navegar = useNavigate();
  const bandeja = BANDEJA[usuario!.role]!;

  const esCocinero = usuario!.role === 'COOK';

  const resumen = useDatos(
    (s) =>
      api.get<{ counts: Record<string, number>; kitchenPlan?: { PENDING: number; CONFIRMED: number } }>(
        '/requests/summary',
        s,
      ),
    [],
  );
  const proximos = useDatos(
    (s) => api.get<{ events: EventoAgenda[] }>('/calendar/upcoming?limit=5', s),
    [],
  );
  const pendientes = useDatos(
    (s) =>
      api.get<{ rows: SolicitudResumen[] }>(
        // El Cocinero solo tiene por hacer las visitas cuyo plan sigue pendiente:
        // una vez confirmado, la visita deja de estar en su bandeja.
        esCocinero
          ? '/requests?status=AUTHORIZED&kitchenPlan=PENDING&pageSize=8'
          : `/requests?status=${bandeja.estados.join(',')}&pageSize=8`,
        s,
      ),
    [usuario!.role],
  );

  const counts = resumen.datos?.counts ?? {};
  const kp = resumen.datos?.kitchenPlan ?? { PENDING: 0, CONFIRMED: 0 };
  const total = Object.values(counts).reduce((a, b) => a + b, 0);

  const fichas: Array<{ etiqueta: string; valor: number; estado?: Estado; acento?: boolean; a?: string }> =
    esCocinero
      ? [
          { etiqueta: 'Pendientes de plan', valor: kp.PENDING, acento: true, a: '/cocina' },
          { etiqueta: 'Plan confirmado', valor: kp.CONFIRMED, a: '/agenda' },
          { etiqueta: 'Autorizadas', valor: kp.PENDING + kp.CONFIRMED, a: '/agenda' },
        ]
      : [
          { etiqueta: 'Todas', valor: total },
          ...(['DRAFT', 'SENT', 'RECEIVED', 'AUTHORIZED', 'CANCELLED'] as Estado[]).map((e) => ({
            etiqueta: ESTADO_ETIQUETA[e],
            valor: counts[e] ?? 0,
            estado: e,
            acento: bandeja.estados.includes(e),
          })),
        ];

  return (
    <>
      <CabeceraPagina
        titulo={`Buen servicio, ${usuario!.name.split(' ')[0]}`}
        acciones={
          usuario!.role === 'SALESPERSON' || usuario!.role === 'ADMIN' ? (
            <Boton variante="clave" onClick={() => navegar('/solicitudes/nueva')}>
              Nueva solicitud
            </Boton>
          ) : null
        }
      />

      {usuario!.role==='SALESPERSON'&&<TareasPendientes/>}
      <div className="pila">
        <div className="rejilla tres recorrido" aria-label="Recorrido de las solicitudes">
          {fichas.map((f) => (
            <button
              key={f.etiqueta}
              className="contador"
              data-acento={f.acento ? 'true' : undefined}
              onClick={() =>
                navegar(f.a ?? (f.estado ? `/solicitudes?estado=${f.estado}` : '/solicitudes'))
              }
            >
              <span className="cifra">{resumen.cargando ? '·' : f.valor}</span>
              <span className="etiqueta">{f.etiqueta}</span>
            </button>
          ))}
        </div>

        <div className="rejilla dos">
          <Tarjeta
            titulo={bandeja.titulo}
            extra={
              <Link className="sutil" to="/solicitudes">
                Ver todas
              </Link>
            }
            sinRelleno
          >
            {pendientes.cargando ? (
              <Cargando />
            ) : pendientes.datos?.rows.length ? (
              <table className="tabla">
                <tbody>
                  {pendientes.datos.rows.map((r) => (
                    <tr
                      key={r.id}
                      data-clicable="true"
                      onClick={() => navegar(`/solicitudes/${r.id}`)}
                    >
                      <td>
                        <div className="codigo">{r.publicCode}</div>
                        <div style={{ fontWeight: 500 }}>{r.client.name}</div>
                        <div className="sutil">
                          {r.client.city ?? '—'} · {r.owner.name}
                        </div>
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <Pildora estado={r.status} />{Boolean(r.rescheduleCount)&&<span className="sutil">Reprogramada</span>}
                        <div className="sutil" style={{ marginTop: 4 }}>
                          {r.finalStart ? franja(r.finalStart, r.finalEnd) : `Creada ${fecha(r.createdAt)}`}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <Vacio titulo={bandeja.vacio} />
            )}
          </Tarjeta>

          <Tarjeta
            titulo="Próximas visitas"
            extra={
              <Link className="sutil" to="/agenda?vista=lista">
                Ver todos los eventos
              </Link>
            }
            sinRelleno
          >
            {proximos.cargando ? (
              <Cargando />
            ) : proximos.datos?.events.length ? (
              <table className="tabla">
                <tbody>
                  {proximos.datos.events.map((e, i) => (
                    <tr
                      key={`${e.requestId}-${i}`}
                      data-clicable={e.requestId ? 'true' : undefined}
                      onClick={() => e.requestId && navegar(`/solicitudes/${e.requestId}`)}
                    >
                      <td>
                        <div style={{ fontWeight: 500 }}>{e.client?.name ?? e.title}</div>
                        <div className="sutil dato">{franja(e.start, e.end)}</div>
                      </td>
                      <td style={{ textAlign: 'right' }} className="sutil">
                        {e.kitchenPlanStatus === 'CONFIRMED' ? 'Cocina lista' : 'Cocina pendiente'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <Vacio titulo="Sin visitas próximas" />
            )}
          </Tarjeta>
        </div>
      </div>
    </>
  );
}
